import React, { useEffect } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useOrganizationAPI } from "@src/api/organization.api";
import { Organization } from "@src/model/organization";
import { useNavigate } from "react-router-dom";
import Alert, { AlertProps } from "@src/common/alert";

interface OrganizationIndexProps { }

const OrganizationIndex: React.FC<OrganizationIndexProps> = (props) => {
    const [data, setData] = React.useState<Organization[]>([]);
    const [filteredData, setFilteredData] = React.useState<Organization[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const [organizationTypes, setOrganizationTypes] = React.useState<string[]>([]);
    const [selectedType, setSelectedType] = React.useState<string>("agency");
    const organizationAPI = useOrganizationAPI();
    const navigate = useNavigate();

    const columns = [
        {
            name: "Name",
            selector: (row: Organization) => row.name || "",
            sortable: true
        },
        {
            name: "Type",
            selector: (row: Organization) => row.organization_type || "",
            sortable: true
        },
        {
            name: "Status",
            cell: (row: Organization) => (
                <span className={`px-2 py-1 rounded text-white ${row.is_active ? "bg-green-500" : "bg-red-500"}`}>
                    {row.is_active ? "Active" : "Inactive"}
                </span>
            ),
            sortable: true
        },
        {
            name: "Actions",
            cell: (row: Organization) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => navigate(`/organizations/edit/${row.id}`)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleStatusToggle(row)}
                        className={`${row.is_active ? "bg-yellow-500 hover:bg-yellow-700" : "bg-green-500 hover:bg-green-700"} text-white font-bold py-1 px-3 rounded text-sm`}
                    >
                        {row.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        Delete
                    </button>
                </div>
            ),
            width: "300px",
        }
    ];

    const fetchOrganizations = () => {
        setLoading(true);
        organizationAPI.getAllOrganizations()
            .then((response) => {
                setData(response);
                setFilteredData(response);
                setLoading(false);
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: "Failed to load organizations: " + error.message
                });
                setLoading(false);
            });
    };

    const fetchOrganizationTypes = () => {
        organizationAPI.getOrganizationTypes()
            .then((types) => {
                setOrganizationTypes(types);
            })
            .catch((error) => {
                console.error("Failed to load organization types:", error);
            });
    };

    const handleStatusToggle = (organization: Organization) => {
        if (!organization.id) return;

        const newStatus = !organization.is_active;
        organizationAPI.updateOrganizationStatus(organization.id, newStatus)
            .then(() => {
                setStatus({
                    type: "success",
                    message: `Organization ${newStatus ? "activated" : "deactivated"} successfully`
                });
                fetchOrganizations();
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: `Failed to ${newStatus ? "activate" : "deactivate"} organization: ${error.message}`
                });
            });
    };

    const handleDelete = (id?: string) => {
        if (!id) return;

        if (window.confirm("Are you sure you want to delete this organization? This action cannot be undone.")) {
            organizationAPI.deleteOrganization(id)
                .then(() => {
                    setStatus({
                        type: "success",
                        message: "Organization deleted successfully"
                    });
                    fetchOrganizations();
                })
                .catch((error) => {
                    setStatus({
                        type: "error",
                        message: "Failed to delete organization: " + error.message
                    });
                });
        }
    };

    useEffect(() => {
        fetchOrganizations();
        fetchOrganizationTypes();
    }, []);

    useEffect(() => {
        if (selectedType === "all") {
            setFilteredData(data);
        } else {
            setFilteredData(data.filter(org => org.organization_type === selectedType));
        }
    }, [selectedType, data]);

    return (
        <AppContainer>
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center gap-2 mb-6">
                    <h1 className="text-2xl font-bold">Organizations</h1>
                    <button
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex-shrink-0 overflow-clip"
                        onClick={() => navigate("/organizations/add")}
                    >
                        <span className="hidden sm:inline">Add Organization</span>
                        <span className="sm:hidden">Add</span>
                    </button>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}

                <div className="mb-4">
                    <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 mb-2">
                        Filter by Type
                    </label>
                    <select
                        id="type-filter"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="all">All Types</option>
                        {organizationTypes.map((type) => (
                            <option key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    progressPending={loading}
                    progressComponent={<div className="py-6">Loading organizations...</div>}
                    noDataComponent={<div className="py-6">No organizations found</div>}
                />
            </div>
        </AppContainer>
    );
};

export default OrganizationIndex;